import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { HTTPError, post } from "common/helpers/HTTP";
import type { CustomerInfo, Invitation, Workspace } from "common/types";

async function handleInvite(
  memberToken: string,
  accept: boolean,
): Promise<Workspace> {
  return (
    await post(`invites/${memberToken}`, {
      accept,
    })
  ).workspace;
}

export default function useInvitationMutations() {
  const queryClient = useQueryClient();

  const {
    mutateAsync: respondToInvitation,
    isPending: isRespondingToInvitation,
  } = useMutation({
    mutationFn: ({
      memberToken,
      accept,
    }: {
      memberToken: string;
      accept: boolean;
    }) => handleInvite(memberToken, accept),
    onSuccess: (workspace, { accept, memberToken }) => {
      if (queryClient.getQueryData(["invitations"])) {
        queryClient.setQueryData<Invitation[]>(
          ["invitations"],
          (prevInvitations) =>
            prevInvitations.filter(({ token }) => token !== memberToken),
        );
      }
      if (accept) {
        queryClient.setQueryData<Workspace[]>(
          ["workspaces"],
          (prevWorkspaces) => [...prevWorkspaces, workspace],
        );
      }
      queryClient.setQueryData<CustomerInfo>(
        ["customerInfo"],
        (prevCustomer) => ({ ...prevCustomer, show_questionnaire: false }),
      );
    },
    onError: (err) => {
      toast.error(
        err instanceof HTTPError && err.type === "UserIsAlreadyMember"
          ? "You are already a member of this workspace"
          : "Something went wrong while responding to the invitation",
      );
    },
  });

  return {
    respondToInvitation,
    isRespondingToInvitation,
  };
}
